import SystemCard from "@/components/SystemCard";
import SectionHeading from "@/components/SectionHeading";
import SEO from "@/components/SEO";
import SystemCombinations from "@/components/SystemCombinations";
import GeoDefinition, { buildFaqJsonLd } from "@/components/GeoDefinition";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SYSTEMS } from "@/lib/constants";

const SYSTEM_DEFINITIONS = [
  {
    name: "PVRA",
    full: "PINE Virtual Reality Arena",
    text: "PVRA ist das große Virtual-Reality-Arena-System für größere Freizeitflächen und professionelle Betreiberstrukturen. Bis zu 16 Spieler auf zwei Playfields, mehrere Erlebnisformate, klare Wiederholbarkeit.",
  },
  {
    name: "AR_ENA",
    full: "Augmented Reality Arena",
    text: "AR_ENA ist das Action-, Bewegungs- und Competition-System von PINE Systems. Sportlich-kompetitive Teamduelle und Reaktionsformate für Gruppenenergie und wiederkehrende Buchungsanlässe.",
  },
  {
    name: "PINE VR",
    full: "Compact Virtual Reality",
    text: "PINE VR ist das kompakte, professionelle VR-Standortprodukt für kleinere Freizeitflächen, Eventlocations und bestehende Indoorbetriebe. Virtuelle Escape Rooms und kooperative Missionen auf kompakter Fläche.",
  },
  {
    name: "Hotel VR",
    full: "Inhouse Gäste Entertainment",
    text: "Hotel VR ist das hospitality-kompatible Inhouse-Erlebnisformat für Hotels, Resorts und touristische Betriebe. Buchbares Gästeangebot für Familien, Tagungen, Schlechtwetter und Rahmenprogramm.",
  },
  {
    name: "PINE Places",
    full: "Digital Brand Spaces",
    text: "PINE Places macht reale Orte digital begehbar und erklärbar. Aus Hotel, Eventlocation, Showroom oder Immobilie wird ein digitaler Markenraum, der schon vor dem Besuch Teil der Customer Journey ist.",
  },
];

const SYSTEME_FAQ = SYSTEM_DEFINITIONS.map((d) => ({
  q: `Was ist ${d.name}?`,
  a: d.text,
}));

const SYSTEME_JSONLD = [
  buildFaqJsonLd(SYSTEME_FAQ),
  {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: "PINE Systems Produktprofile",
    hasDefinedTerm: SYSTEM_DEFINITIONS.map((d) => ({
      "@type": "DefinedTerm",
      name: d.name,
      alternateName: d.full,
      description: d.text,
    })),
  },
];

const COMPARE = [
  {
    code: "01",
    name: "PVRA",
    use: "Für größere Freizeitflächen und Betreiber, die ein vollwertiges Arena-Angebot mit mehreren Erlebnisformaten aufbauen möchten.",
    benefit: "Breite Zielgruppen, mehrere Buchungsanlässe, bis zu 16 Spieler, zwei Playfields.",
    typical: "Freizeitstandorte, Gruppenangebote, Firmen, Familien, Events.",
    href: "/pvra",
  },
  {
    code: "02",
    name: "AR_ENA",
    use: "Für Betreiber, die Bewegung, Competition und Teamduelle zum Kern des Erlebnisses machen wollen.",
    benefit: "Sportliche Aktivierung, hohe Gruppenenergie, Action- und Wettbewerbslogik.",
    typical: "Actionflächen, sportliche Freizeitbetriebe, Teenager, Erwachsene, Teams.",
    href: "/ar-ena",
  },
  {
    code: "03",
    name: "PINE VR",
    use: "Für kleinere Standorte und Eventlocations, die ein kompaktes, betreutes VR-Erlebnisformat integrieren möchten.",
    benefit: "VR-Erlebnis auf kleinerer Fläche, kooperative Missionen, virtuelle Escape Rooms, Gruppenformate.",
    typical: "Eventlocations, kleinere Freizeitflächen, bestehende Indoorangebote.",
    href: "/pine-vr",
  },
  {
    code: "04",
    name: "Hotel VR",
    use: "Für Hotels, Resorts und touristische Betriebe, die Gästen ein buchbares Inhouse-Erlebnis bieten möchten.",
    benefit: "Schlechtwetterangebot, Familienerlebnis, Rahmenprogramm, Aufenthaltsqualität, Zusatzangebot im Haus.",
    typical: "Hotels, Resorts, Ferienanlagen, Tagungshotels, Campingparks.",
    href: "/hotel-vr",
  },
  {
    code: "05",
    name: "PINE Places",
    use: "Für reale Orte, die digital erlebbar und Teil der Customer Journey werden sollen.",
    benefit: "Digitale Standortkopie, Markenraum, Orientierung, emotionale Entscheidungshilfe, digitale Präsenz.",
    typical: "Hotels, Immobilien, Showrooms, Eventlocations, Freizeitstandorte, touristische Orte, Markenräume.",
    href: "/pine-places",
  },
];

export default function Systeme() {
  return (
    <>
      <SEO
        title="PINE Systems Profile – PVRA, AR_ENA, PINE VR, Hotel VR und PINE Places"
        description="Fünf Systemprofile für unterschiedliche Standortlogiken: Virtual-Reality-Arena, Action-Arena, kompaktes VR-Erlebnis, Hotel VR und digitale Markenräume."
        jsonLd={SYSTEME_JSONLD}
      />
      <section className="relative border-b border-[rgba(255,255,255,0.08)] overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-24">
          <div className="overline">Systeme</div>
          <h1 className="font-display text-4xl md:text-6xl uppercase mt-4 max-w-5xl leading-[1.05]">
            Fünf Systemprofile. <br className="hidden md:block" />
            <span className="text-[#18E096]">Ein Ziel.</span>
          </h1>
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            PINE Systems prüft Fläche, Betreiberlogik, Zielgruppe, Nutzungspotenzial und Customer
            Journey und leitet daraus das passende Systemprofil ab.
          </p>
        </div>
      </section>

      {/* Decision intro */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Entscheidungshilfe</div>
            <h2 className="font-display text-3xl md:text-4xl uppercase leading-[1.08]">
              Nicht jeder Standort braucht <span className="text-[#18E096]">dieselbe Antwort.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-[#AEB7B7] text-base md:text-lg leading-relaxed">
              Ein Hotel benötigt eine andere Erlebnislogik als ein Actionanbieter. Eine große
              Freizeitfläche braucht andere Abläufe als eine kompakte Eventlocation. Und ein
              realer Markenort, der digital sichtbar werden soll, folgt einer anderen Customer
              Journey als eine VR-Arena.
            </p>
            <p className="text-white text-base md:text-lg leading-relaxed mt-5">
              PINE Systems arbeitet deshalb mit fünf klar getrennten Systemprofilen.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <SectionHeading
            overline="Die Systemprofile"
            title="Im Überblick."
            subtitle="Jedes Systemprofil beantwortet eine andere Frage: Welche Zielgruppe? Welche Fläche? Welche Customer Journey? Welche Buchungsanlässe?"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-14">
            {SYSTEMS.map((s, i) => (
              <SystemCard key={s.slug} s={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="border-t border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <SectionHeading overline="Vergleich" title="Die Systemprofile im Vergleich." />
          <div className="mt-12 border border-[rgba(255,255,255,0.08)] divide-y divide-[rgba(255,255,255,0.08)]">
            {COMPARE.map((c) => (
              <Link
                to={c.href}
                key={c.code}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 p-6 md:p-8 hover:bg-[#141819] transition-colors group"
                data-testid={`compare-${c.name.toLowerCase().replace("_", "-").replace(" ", "-")}`}
              >
                <div className="md:col-span-2 flex items-center gap-3">
                  <span className="font-display text-[#18E096] text-xl">{c.code}</span>
                  <span className="font-display text-xl md:text-2xl uppercase">{c.name}</span>
                </div>
                <div className="md:col-span-4">
                  <div className="overline mb-1">Für wen</div>
                  <p className="text-[#AEB7B7] text-sm leading-relaxed">{c.use}</p>
                </div>
                <div className="md:col-span-3">
                  <div className="overline mb-1">Nutzen</div>
                  <p className="text-white text-sm leading-relaxed">{c.benefit}</p>
                </div>
                <div className="md:col-span-3">
                  <div className="overline mb-1">Typische Anwendung</div>
                  <p className="text-[#AEB7B7] text-sm leading-relaxed">{c.typical}</p>
                </div>
                <div className="md:col-span-12 mt-2 flex items-center justify-end text-xs uppercase tracking-[0.2em] text-[#AEB7B7] group-hover:text-[#18E096] transition-colors">
                  Details ansehen <ArrowUpRight size={14} strokeWidth={1.5} className="ml-2" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <SectionHeading
              overline="Nicht sicher?"
              title="Lassen Sie uns Ihr Standortpotenzial prüfen."
              subtitle="Statt direkter Empfehlung beginnt der Prozess mit einer strukturierten Einschätzung."
            />
          </div>
          <div className="lg:col-span-5 lg:justify-self-end flex flex-wrap gap-3">
            <Link to="/standort-pruefen" className="btn-primary" data-testid="systeme-cta">
              Systemfit prüfen lassen <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/kontakt" className="btn-secondary">
              Erstgespräch anfragen
            </Link>
          </div>
        </div>
      </section>

      <SystemCombinations />

      {/* GEO / AI SEARCH READINESS */}
      <section
        className="border-t border-[rgba(255,255,255,0.08)] bg-[#0B0F10]"
        data-testid="systeme-geo-definitions"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="overline mb-4">GEO · Definitionen</div>
          <h2 className="font-display text-2xl md:text-4xl uppercase leading-[1.1] max-w-3xl">
            Die fünf Systemprofile <span className="text-[#18E096]">in einem Satz.</span>
          </h2>
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base leading-relaxed">
            Kurzdefinitionen, die jedes Systemprofil unverwechselbar einordnen – für klare
            Entscheidungen und gute Auffindbarkeit in KI-Suchsystemen.
          </p>

          <dl className="mt-10 divide-y divide-[rgba(255,255,255,0.08)] border-t border-b border-[rgba(255,255,255,0.08)]">
            {SYSTEM_DEFINITIONS.map((d) => (
              <div
                key={d.name}
                className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4"
                data-testid={`geo-def-${d.name.toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-")}`}
              >
                <dt className="md:col-span-3">
                  <div className="font-display text-lg md:text-xl uppercase text-white">
                    {d.name}
                  </div>
                  <div className="text-[#18E096] text-xs uppercase tracking-[0.18em] mt-1">
                    {d.full}
                  </div>
                </dt>
                <dd className="md:col-span-9 text-[#AEB7B7] text-sm md:text-base leading-relaxed">
                  {d.text}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  );
}
